import type { Chapter, ProfessionTag } from './types.js';

/**
 * THE THIRTY — five games for every post, each one about that post's job (Marek, 2026-09-29:
 * "there are only three games and they repeat, they are not tailored to each profession and not
 * creative… even if there are 12 players, no one should play the same game twice").
 *
 * Every stop deals twelve games: each post's first holder plays one of its five, a second holder
 * another. Over the evening the first holder plays the post's games in order and the second holder
 * the same five two along, so nobody repeats a game, the two holders of a post never play the same
 * game at the same stop, and a full table of twelve plays twelve different games at every stop.
 *
 *   stop c:  holder A plays game (c − 1),  holder B plays game (c + 1) mod 5
 *
 * Every game plays in three short rounds, each a little harder or faster than the last. The set
 * sits behind a switch in the room (`ShowState.gameSet`), and the evening keeps the classic six
 * until the whole thirty has passed the test bench ("all or nothing", Marek, 2026-09-29).
 */

/** Which set of games the evening deals. */
export type GameSet = 'classic' | 'thirty';
export const GAME_SETS: readonly GameSet[] = ['classic', 'thirty'];

export type ThirtyId =
  | 'nav.gaps'
  | 'nav.stars'
  | 'nav.rocks'
  | 'nav.heading'
  | 'nav.dock'
  | 'eng.coils'
  | 'eng.pressure'
  | 'eng.wires'
  | 'eng.pipes'
  | 'eng.fuses'
  | 'bio.vine'
  | 'bio.bees'
  | 'bio.seeds'
  | 'bio.mushrooms'
  | 'bio.water'
  | 'sig.echo'
  | 'sig.tune'
  | 'sig.wave'
  | 'sig.code'
  | 'sig.dish'
  | 'arc.pairs'
  | 'arc.mend'
  | 'arc.shadow'
  | 'arc.changed'
  | 'arc.order'
  | 'qtm.pour'
  | 'qtm.count'
  | 'qtm.scales'
  | 'qtm.pack'
  | 'qtm.shares';

/** Which of a post's two games this is at a stop: the first holder's, or a second holder's. */
export type Slot = 'A' | 'B';

export interface ThirtyGame {
  id: ThirtyId;
  post: ProfessionTag;
  /** The game's own name, whatever stop it is played at (practice page, logs). */
  name: string;
  nameNo: string;
  /** One plain line: what you do. */
  hint: string;
  /**
   * What an ordinary guest takes over all three rounds, from the how-to card's START to WELL
   * DONE, one mistake included. An estimate from how the game is built; tune after a dry run.
   */
  parMs: number;
  /** The title at each of the two stops it is played at, English and bokmål. */
  titles: Partial<Record<Exclude<Chapter, 0>, readonly [string, string]>>;
}

const g = (
  id: ThirtyId,
  post: ProfessionTag,
  name: readonly [string, string],
  hint: string,
  parMs: number,
  titles: ThirtyGame['titles'],
): ThirtyGame => ({ id, post, name: name[0], nameNo: name[1], hint, parMs, titles });

/**
 * Each post's five, in the order its first holder meets them (stop 1 to 5). A game's two stops
 * are its first holder's (its place + 1) and a second holder's (4, 5, 1, 2, 3 for places 0–4).
 */
export const THIRTY: Record<ProfessionTag, readonly ThirtyGame[]> = {
  NAV: [
    g('nav.gaps', 'NAV', ['Fly the gaps', 'Fly gjennom åpningene'], 'Tap to fly up. Fly through the gaps.', 40_000, {
      1: ['Fly out of orbit', 'Fly ut i verdensrommet'],
      4: ['Fly the tunnels', 'Fly gjennom tunnelene'],
    }),
    g('nav.stars', 'NAV', ['Join the stars', 'Koble stjernene'], 'Tap the stars in order to draw the course.', 35_000, {
      2: ['Plot a course past the city', 'Legg kursen forbi byen'],
      5: ['Read the stars again', 'Les stjernene igjen'],
    }),
    g('nav.rocks', 'NAV', ['Asteroid field', 'Asteroidefeltet'], 'Slide the ship left and right past the rocks.', 45_000, {
      3: ['Steer through the wreckage', 'Styr gjennom vrakrestene'],
      1: ['Clear the launch debris', 'Kom deg forbi romskrotet'],
    }),
    g('nav.heading', 'NAV', ['Set the heading', 'Sett kursen'], 'Stop the turning needle on the marked heading.', 30_000, {
      4: ['Find north under the sand', 'Finn nord under sanden'],
      2: ['Set course for the colonies', 'Sett kurs mot koloniene'],
    }),
    g('nav.dock', 'NAV', ['Dock the ship', 'Legg til kai'], 'Hold to brake. Touch down slowly on the pad.', 35_000, {
      5: ['Bring her down on the surface', 'Sett skipet ned på overflaten'],
      3: ['Dock under fire', 'Legg til kai under angrep'],
    }),
  ],
  ENG: [
    g('eng.coils', 'ENG', ['Two coils', 'To spoler'], 'Both thumbs: tap the side whose light reaches the line.', 35_000, {
      1: ['Start the engine', 'Start motoren'],
      4: ['Keep the pumps beating', 'Hold pumpene i takt'],
    }),
    g('eng.pressure', 'ENG', ['Hold the pressure', 'Hold trykket'], 'Hold to pump, let go to vent. Keep the needle in the green.', 30_000, {
      2: ['Keep the factory at pressure', 'Hold trykket i fabrikken'],
      5: ['Let the old engine rest', 'La den gamle motoren hvile'],
    }),
    g('eng.wires', 'ENG', ['Reconnect the wires', 'Koble ledningene'], 'Drag each wire to the socket with its colour and sign.', 40_000, {
      3: ['Rewire the damaged deck', 'Koble om det skadde dekket'],
      1: ['Wire the launch panel', 'Koble opp startpanelet'],
    }),
    g('eng.pipes', 'ENG', ['Turn the pipes', 'Snu rørene'], 'Tap pipes to turn them until the flow reaches the engine.', 45_000, {
      4: ['Get the water flowing', 'Få vannet til å renne'],
      2: ['Connect the factory pipes', 'Koble sammen fabrikkrørene'],
    }),
    g('eng.fuses', 'ENG', ['Swap the fuses', 'Bytt sikringene'], 'Tap each fuse that glows red before it blows.', 38_000, {
      5: ['Swap the last fuses', 'Bytt de siste sikringene'],
      3: ['Swap the blown fuses', 'Bytt de brente sikringene'],
    }),
  ],
  BIO: [
    g('bio.vine', 'BIO', ['Grow the vine', 'Dyrk ranken'], 'Steer the plant to the seeds.', 45_000, {
      1: ['Grow the first plant', 'Dyrk den første planten'],
      4: ['Grow roots through the sand', 'La røttene gro gjennom sanden'],
    }),
    g('bio.bees', 'BIO', ['Pollinate', 'Pollinér'], 'Fly the bee from a flower to one that matches it.', 40_000, {
      2: ['Pollinate the rooftop gardens', 'Pollinér takhagene'],
      5: ['Pollinate the first orchard', 'Pollinér den første frukthagen'],
    }),
    g('bio.seeds', 'BIO', ['Sort the seeds', 'Sorter frøene'], 'Tap left or right to drop each seed in its pot.', 45_000, {
      3: ['Save the seeds', 'Redd frøene'],
      1: ['Pack the seed vault', 'Pakk frøhvelvet'],
    }),
    g('bio.mushrooms', 'BIO', ['Mushrooms in the dark', 'Sopp i mørket'], 'Slide your finger to light the dark. Tap the mushrooms.', 45_000, {
      4: ['Pick mushrooms in the dark', 'Plukk sopp i mørket'],
      2: ['Find the glowing mushrooms', 'Finn de lysende soppene'],
    }),
    g('bio.water', 'BIO', ['Keep them alive', 'Hold dem i live'], 'Tap a plant to water it before it wilts.', 45_000, {
      5: ['Keep the new garden alive', 'Hold den nye hagen i live'],
      3: ['Keep the sick bay plants alive', 'Hold plantene i sykestua i live'],
    }),
  ],
  SIG: [
    g('sig.echo', 'SIG', ['Copy the signal', 'Kopier signalet'], 'Watch the lights, then tap the same order.', 35_000, {
      1: ['Copy the launch signal', 'Kopier startsignalet'],
      4: ['Call the caravan', 'Rop opp karavanen'],
    }),
    g('sig.tune', 'SIG', ['Tune the radio', 'Still inn radioen'], 'Turn the dial until the signal is clear, and hold it there.', 35_000, {
      2: ['Tune in to the city', 'Finn byens kanal'],
      5: ['Listen for the surface', 'Lytt etter overflaten'],
    }),
    g('sig.wave', 'SIG', ['Match the wave', 'Treff bølgen'], 'Slide the two controls until your wave lies on the target.', 40_000, {
      3: ['Match the shield wave', 'Treff skjoldbølgen'],
      1: ["Catch Earth's last broadcast", 'Fang Jordas siste sending'],
    }),
    g('sig.code', 'SIG', ['Crack the code', 'Knekk koden'], 'Use the key to turn the symbols into letters.', 45_000, {
      4: ["Crack the caravan's code", 'Knekk karavanens kode'],
      2: ['Crack the billboard code', 'Knekk reklamekoden'],
    }),
    // In place of PASS IT ON (Marek, 2026-10-06: "it's not working" — showing a code to another guest across the
    // table), a game played alone like every other.
    g('sig.dish', 'SIG', ['Aim the dish', 'Sikt inn parabolen'], 'Drag the beam onto the satellite and keep it there until it locks.', 30_000, {
      5: ['Find the old satellite', 'Finn den gamle satellitten'],
      3: ['Lock on to the fleet', 'Lås inn på flåten'],
    }),
  ],
  ARC: [
    g('arc.pairs', 'ARC', ['Find the pairs', 'Finn parene'], 'Flip two cards. Find the pairs.', 45_000, {
      1: ['Sort the last photos of Earth', 'Sorter de siste bildene av Jorda'],
      4: ['Match the spare parts', 'Finn like reservedeler'],
    }),
    g('arc.mend', 'ARC', ['Mend the picture', 'Reparer bildet'], 'Tap the tiles to turn them until the picture is whole.', 40_000, {
      2: ['Mend the torn poster', 'Reparer den revne plakaten'],
      5: ['Mend the old family photo', 'Reparer det gamle familiebildet'],
    }),
    g('arc.shadow', 'ARC', ['Match the shadow', 'Finn skyggen'], 'Pick the outline that fits the object.', 35_000, {
      3: ['Name the wreckage', 'Kjenn igjen vrakdelene'],
      1: ['Label the museum pieces', 'Merk museumsgjenstandene'],
    }),
    g('arc.changed', 'ARC', ['What changed?', 'Hva er endret?'], 'Remember the shelf. Tap the thing that changed.', 48_000, {
      4: ['Check the store room', 'Sjekk lageret'],
      2: ['Spot the forged record', 'Finn den forfalskede posten'],
    }),
    g('arc.order', 'ARC', ['In order', 'I rekkefølge'], 'Tap the pictures in the order they happened.', 40_000, {
      5: ['Put the archive in order', 'Sett arkivet i rekkefølge'],
      3: ['Order the evacuation files', 'Sorter evakueringsmappene'],
    }),
  ],
  QTM: [
    g('qtm.pour', 'QTM', ['Pour to the line', 'Skjenk til streken'], 'Hold to pour. Let go on the line.', 35_000, {
      1: ['Pour the first toast', 'Skjenk den første skålen'],
      4: ['Pour the underground brew', 'Skjenk brygget under sanden'],
    }),
    g('qtm.count', 'QTM', ['Count the stock', 'Tell lageret'], 'Count the marked crates as they roll past.', 55_000, {
      2: ['Count the party stock', 'Tell festlageret'],
      5: ['Count the harvest', 'Tell avlingen'],
    }),
    g('qtm.scales', 'QTM', ['Balance the scales', 'Balanser vekten'], 'Weights on the empty pan until it is level. Not every weight is needed.', 45_000, {
      3: ['Ration the water fairly', 'Del vannet rettferdig'],
      1: ['Weigh the cargo', 'Vei lasten'],
    }),
    g('qtm.pack', 'QTM', ['Pack the crate', 'Pakk kassen'], 'Drag the blocks in until the crate is full.', 50_000, {
      4: ['Pack the caravan', 'Pakk karavanen'],
      2: ['Pack the export crates', 'Pakk eksportkassene'],
    }),
    g('qtm.shares', 'QTM', ['Fair shares', 'Rettferdig deling'], 'The bars slide. Tap each one where it splits evenly.', 35_000, {
      5: ['Share out the last power', 'Del ut den siste kraften'],
      3: ['Share the rations', 'Del rasjonene'],
    }),
  ],
};

export const THIRTY_POSTS: readonly ProfessionTag[] = ['NAV', 'ENG', 'BIO', 'SIG', 'ARC', 'QTM'];

/** Every game of the thirty, post by post, each in its first holder's order. */
export const THIRTY_GAMES: readonly ThirtyGame[] = THIRTY_POSTS.flatMap((post) => THIRTY[post]);

export function thirtyGame(id: ThirtyId): ThirtyGame {
  const game = THIRTY_GAMES.find((x) => x.id === id);
  if (!game) throw new Error(`no such game: ${id}`);
  return game;
}

/** Which of a post's five a holder plays at a stop. */
export function thirtyPlace(chapter: Exclude<Chapter, 0>, slot: Slot): number {
  return slot === 'A' ? chapter - 1 : (chapter + 1) % 5;
}

/** The two stops a game is played at: its first holder's, and a second holder's. */
export function thirtyStops(place: number): { A: Exclude<Chapter, 0>; B: Exclude<Chapter, 0> } {
  return { A: (place + 1) as Exclude<Chapter, 0>, B: ([4, 5, 1, 2, 3] as const)[place]! };
}
